/* ==========================================================================
   form-relay.js — posts theme contact forms to the form relay (SMTP to the
   recipient list set per form on the server) instead of Shopify's /contact.

   Loaded only by sections that render a relay form, and only when Theme
   settings → Forms → "Send forms through the form relay" is on with a URL.

   Opt-in markup:
     <form data-relay-form="contact" data-relay-url="https://…/send.php">
       <input data-relay-name="first_name" …>     sent as first_name
       <input data-relay-hp …>                    an existing honeypot (copied into _hp)
       <template data-relay-success>…</template>  the success markup Shopify
                                                  renders after contact_posted
       <div data-relay-errors hidden>             the form's error area
     data-relay-manual: the form's own script calls FormRelay.send() (product
     inquiry popup); it is not bound here.

   Request: JSON { form_id, <fields>, page_url, product_url (if not set),
   _hp, _ts }. _ts = when the form was set up; the relay drops submits faster
   than 3 s as bots, so a faster submit waits until 3 s have passed.

   Responses: 200 → success markup + reset + "form-relay:success" { formId }
   on the form (bubbles); 422 → message under each field + focus the first;
   429 / other 4xx → the relay's message in the error area; network error,
   15 s timeout or 5xx → native Shopify submit (form.submit()), so no lead is
   lost. form.submit() fires no submit event, so it can't loop.

   Shopify's contact-form hCaptcha (storefront-forms-hcaptcha) adds a submit
   listener to each contact form that always preventDefault()s and posts the
   form itself after the captcha, and replaces form.submit with that same
   captcha-then-post function. So the relay listens in the CAPTURE phase on
   the form (runs before Shopify's listener) and stops the event there:
   otherwise the lead would go out twice. The fallback form.submit() is then
   Shopify's own captcha-guarded submit, exactly what a native submit does.
   The section's own submit listeners are skipped too (a filled honeypot is
   sent as _hp, which the relay drops).
   ========================================================================== */
(function () {
  'use strict';

  if (window.FormRelay) return;

  var TIMEOUT = 15000;
  var MIN_FILL = 3000;
  var GENERIC_ERROR = 'Sorry, your message could not be sent. Please try again.';

  function setup(form) {
    if (form.__relayTs) return;
    form.__relayTs = Date.now();
    if (form.querySelector('input[name="_hp"]')) return;
    var wrap = document.createElement('div');
    wrap.className = 'form-relay__hp';
    wrap.setAttribute('aria-hidden', 'true');
    var input = document.createElement('input');
    input.type = 'text';
    input.name = '_hp';
    input.tabIndex = -1;
    input.autocomplete = 'off';
    input.value = '';
    wrap.appendChild(input);
    form.appendChild(wrap);
  }

  function fieldValue(el) {
    if (el.type === 'checkbox') return el.checked;
    if (el.type === 'radio') return el.checked ? el.value : null;
    return el.value.trim();
  }

  // Collects the relay payload from [data-relay-name] fields.
  function payload(form, formId) {
    var data = { form_id: formId };
    Array.prototype.forEach.call(form.querySelectorAll('[data-relay-name]'), function (el) {
      if (el.disabled) return;
      var name = el.getAttribute('data-relay-name');
      var value = fieldValue(el);
      if (value === null) {
        if (!(name in data)) data[name] = '';
        return;
      }
      data[name] = value;
    });
    var page = window.location.origin + window.location.pathname;
    data.page_url = page;
    if (!data.product_url) data.product_url = page;
    var hp = '';
    Array.prototype.forEach.call(form.querySelectorAll('input[name="_hp"], [data-relay-hp]'), function (el) {
      if (el.value) hp = el.value;
    });
    data._hp = hp;
    data._ts = form.__relayTs || Date.now();
    return data;
  }

  function wait(ms) {
    return new Promise(function (resolve) { setTimeout(resolve, ms); });
  }

  // Posts the form. Resolves { status: 'ok' | 'fields' | 'message' | 'fallback', fields, message, payload }.
  function send(form, options) {
    options = options || {};
    setup(form);
    var url = options.url || form.getAttribute('data-relay-url');
    var formId = options.formId || form.getAttribute('data-relay-form');
    var data = payload(form, formId);
    var early = MIN_FILL - (Date.now() - data._ts);

    return wait(early > 0 ? early : 0).then(function () {
      var controller = typeof AbortController === 'function' ? new AbortController() : null;
      var timer = setTimeout(function () { if (controller) controller.abort(); }, TIMEOUT);
      return fetch(url, {
        method: 'POST',
        mode: 'cors',
        credentials: 'omit',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(data),
        signal: controller ? controller.signal : undefined
      }).then(function (response) {
        clearTimeout(timer);
        return response.json().catch(function () { return {}; }).then(function (json) {
          if (response.ok && json.ok) return { status: 'ok', payload: data };
          if (response.status >= 500) return { status: 'fallback', payload: data };
          if (response.status === 422 && json.fields) {
            return { status: 'fields', fields: json.fields, message: json.message || '', payload: data };
          }
          return { status: 'message', message: json.message || GENERIC_ERROR, payload: data };
        });
      }, function () {
        clearTimeout(timer);
        return { status: 'fallback', payload: data };
      });
    });
  }

  /* ---- Default UI for bound forms ---------------------------------------- */

  function errorBox(form) {
    return form.querySelector('[data-relay-errors]');
  }

  function clearErrors(form) {
    var box = errorBox(form);
    if (box) { box.textContent = ''; box.hidden = true; }
    clearFieldErrors(form);
  }

  function clearFieldErrors(form) {
    Array.prototype.forEach.call(form.querySelectorAll('.form-relay__field-error'), function (p) { p.remove(); });
    Array.prototype.forEach.call(form.querySelectorAll('[data-relay-name][aria-invalid="true"]'), function (el) {
      el.removeAttribute('aria-invalid');
      var described = (el.getAttribute('aria-describedby') || '').split(' ').filter(function (id) {
        return id.indexOf('form-relay-error-') !== 0;
      }).join(' ');
      if (described) el.setAttribute('aria-describedby', described);
      else el.removeAttribute('aria-describedby');
    });
  }

  function showMessage(form, message) {
    var box = errorBox(form);
    if (!box) return;
    box.textContent = message;
    box.hidden = false;
    box.scrollIntoView({ block: 'nearest' });
  }

  // One message under each field named in a 422; returns the first field (or null).
  function markFields(form, fields) {
    var first = null;
    Object.keys(fields).forEach(function (name) {
      var el = form.querySelector('[data-relay-name="' + name + '"]');
      if (!el) return;
      var id = 'form-relay-error-' + Math.random().toString(36).slice(2, 9);
      var p = document.createElement('p');
      p.className = 'form-relay__field-error';
      p.id = id;
      p.textContent = fields[name];
      var anchor = el.closest('[data-relay-field]') || el.parentElement;
      anchor.appendChild(p);
      el.setAttribute('aria-invalid', 'true');
      el.setAttribute('aria-describedby', ((el.getAttribute('aria-describedby') || '') + ' ' + id).trim());
      if (!first) first = el;
    });
    return first;
  }

  // 422: messages under the fields + the relay's summary, then focus the first invalid field.
  function showFieldErrors(form, fields, message) {
    var first = markFields(form, fields);
    if (message || !first) showMessage(form, message || GENERIC_ERROR);
    if (first) first.focus();
  }

  function setPending(form, on) {
    form.classList.toggle('is-submitting', on);
    form.setAttribute('aria-busy', on ? 'true' : 'false');
    Array.prototype.forEach.call(form.querySelectorAll('[type="submit"]'), function (button) {
      button.disabled = on;
      if (on) button.setAttribute('aria-disabled', 'true');
      else button.removeAttribute('aria-disabled');
    });
  }

  function showSuccess(form) {
    var template = form.querySelector('template[data-relay-success]');
    try { form.reset(); } catch (e) { /* ignore */ }
    if (template) {
      var content = template.content.cloneNode(true);
      form.replaceChildren(content);
      var focusTarget = form.querySelector('[tabindex="-1"], [role="status"], [role="alert"]') || form.firstElementChild;
      if (focusTarget) {
        if (!focusTarget.hasAttribute('tabindex')) focusTarget.setAttribute('tabindex', '-1');
        focusTarget.focus({ preventScroll: true });
        var rect = focusTarget.getBoundingClientRect();
        if (rect.top < 0 || rect.top > window.innerHeight) focusTarget.scrollIntoView({ block: 'center' });
      }
    }
  }

  function onSubmit(event) {
    var form = event.currentTarget;
    if (event.target !== form) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    // data-relay-manual: the form's own script runs the relay (product inquiry popup).
    if (form.hasAttribute('data-relay-manual')) {
      form.dispatchEvent(new CustomEvent('form-relay:submit'));
      return;
    }
    if (form.__relayPending) return;
    form.__relayPending = true;
    clearErrors(form);
    setPending(form, true);

    send(form).then(function (result) {
      form.__relayPending = false;
      if (result.status === 'ok') {
        setPending(form, false);
        form.dispatchEvent(new CustomEvent('form-relay:success', {
          bubbles: true,
          detail: { formId: result.payload.form_id }
        }));
        showSuccess(form);
        return;
      }
      if (result.status === 'fallback') {
        // Relay unreachable / timed out / 5xx: Shopify's own submit, as before.
        form.submit();
        return;
      }
      setPending(form, false);
      if (result.status === 'fields') showFieldErrors(form, result.fields, result.message);
      else showMessage(form, result.message);
    });
  }

  function bind(form) {
    if (form.__relayBound) return;
    if (!form.getAttribute('data-relay-form') || !form.getAttribute('data-relay-url')) return;
    form.__relayBound = true;
    setup(form);
    form.addEventListener('submit', onSubmit, true);
  }

  function bindAll(root) {
    Array.prototype.forEach.call((root || document).querySelectorAll('form[data-relay-form]'), bind);
  }

  window.FormRelay = {
    send: send,
    setup: setup,
    markFields: markFields,
    clearFieldErrors: clearFieldErrors,
    GENERIC_ERROR: GENERIC_ERROR
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { bindAll(); });
  else bindAll();
  document.addEventListener('shopify:section:load', function (event) { bindAll(event.target); });
})();
